import React, { useMemo, useState } from "react";
import { useQueryClient } from '@tanstack/react-query'
import { Button, Row, Col, Tooltip, Table, Input, Typography } from 'antd';
import { FaPlusCircle, FaPencilAlt } from 'react-icons/fa';
import auth from "../../services/authService";
import PlanModal from "./PlanModal";
import usePlanServices from "../../hooks/usePlanServices";
import { normalized } from './../../utils/string';
import { numberFormat } from './../../utils/numberFormat';

const { Title } = Typography;

function Plan() {
    const queryClient = useQueryClient();

    const [modalVisible, setModalVisible] = useState(false);
    const [plan, setPlan] = useState(null);
    const [search, setSearch] = useState(null);

    const { data, isLoading } = usePlanServices();

    const handleClickModalNew = () => {
        setPlan(null);
        setModalVisible(true);
    }

    const handleClickModalEdit = (record) => {
        setPlan(record);
        setModalVisible(true);
    }

    const handleOk = (item) => {
        if (plan) {
            // Actualiza plan en la cache
            queryClient.setQueryData(['plan-services'], (oldData) => {
                if(!oldData) return [item];
                const tmp = oldData.filter(c => c.productId !== item.productId);
                return [...tmp, item];
            });
        } else {
            // Agregar el nuevo plan a la cache de React Query
            queryClient.setQueryData(['plan-services'], (oldData) => oldData ? [...oldData, item] : [item]);
        }

        setModalVisible(false);
    }

    const handleModalCancel = () => {
        setPlan(null);
        setModalVisible(false);
    }

    const dataSource = useMemo(() => {
        let tmp = data ?? [];

        // Search
        if (search) {
            tmp = tmp.filter((entry) =>
                normalized(entry.productId).includes(normalized(search)) ||
                normalized(entry.name).includes(normalized(search))
            );
        }

        return tmp;
    }, [data, search]);

    const columns = [
        {
            title: 'ID',
            dataIndex: 'productId',
            width: 80,
            defaultSortOrder: 'ascend',
            sorter: (a, b) => parseInt(a.productId, 10) - parseInt(b.productId, 10)
        },
        { title: 'Nombre', dataIndex: 'name' },
        {
            title: 'Precio',
            dataIndex: 'price',
            align: 'center',
            with: 100,
            sorter: (a, b) => a.price - b.price,
            render: (value) => numberFormat(value)
        },
        { title: 'Días descuento', dataIndex: 'discountDays', align: 'center', sorter: (a, b) => a.discountDays - b.discountDays,},
        {
            title: 'Monto descuento',
            dataIndex: 'discountAmount',
            align: 'center',
            sorter: (a, b) => a.discountAmount - b.discountAmount,
            render: (value) => numberFormat(value)
        },
        {
            title: '',
            align: 'center',
            width: 100,
            render: (_, record) => (
                <Tooltip title="Editar">
                    <Button type="primary" shape="circle" onClick={() => handleClickModalEdit(record)}
                        icon={<FaPencilAlt />} disabled={!auth.isInRoles('PLAN_UPDATE')} />
                </Tooltip>)
        },
    ];

	return <>
        <PlanModal
            visible={modalVisible}
            plan={plan} onOk={handleOk}
            onCancel={handleModalCancel}
            dataPlans={data} />
        <Row gutter={[24, 24]} style={{ marginTop: '2rem', marginLeft: '2rem', marginRight: '2rem' }}>
			<Col span={24}>
				<Title level={2}>Planes</Title>
				<hr className="hr-title" />
			</Col>
			<Col span={24}>
				<Row justify="space-between" align="middle">
					<Col>
				        <Input placeholder="Buscar" onChange={({ target }) => setSearch(target.value)} />
					</Col>
					<Col>
                        <Button type="primary" icon={<FaPlusCircle />} shape="round" onClick={handleClickModalNew}
                            disabled={!auth.isInRoles('PLAN_CREATE')}>
                            &nbsp; Nuevo
                        </Button>
					</Col>
				</Row>
			</Col>
			<Col span={24}>
				<Table rowKey="productId" dataSource={dataSource} columns={columns} loading={isLoading} />
			</Col>
		</Row>
    </>
}

export default Plan;